import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Tooltip, Space } from "antd";
import { FaEdit, FaTrash, FaEye } from "react-icons/fa";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useNavigate } from "react-router-dom";
import rbacService from "../../services/rbacService";
import { useAuth } from "../../contexts/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import PermissionGuard from "../Guards/PermissionGuard";

const RolesGrid = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await rbacService.getAllRoles();
      setRoles(data);
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در بارگذاری نقش‌ها
        </p>
      );
      if (error.response && error.response.status === 401) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDelete = (roleId, roleName) => {
    toast.warn(
      <div className="text-right font-custom">
        <p>آیا از حذف نقش "{roleName}" مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-2">
          <button
            className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
            onClick={() => confirmDelete(roleId)}
          >
            حذف
          </button>
          <button
            className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
            onClick={() => toast.dismiss()}
          >
            انصراف
          </button>
        </div>
      </div>,
      { autoClose: false, icon: false }
    );
  };

  const confirmDelete = async (roleId) => {
    try {
      toast.dismiss();
      await rbacService.deleteRole(roleId);
      setRoles((prevRoles) => prevRoles.filter((role) => role.id !== roleId));
      toast.success(
        <p dir="rtl" className="font-custom">
          نقش با موفقیت حذف شد!
        </p>
      );
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در حذف نقش!
        </p>
      );
    }
  };

  const columns = [
    {
      title: "عملیات",
      key: "actions",
      fixed: "left",
      width: 120,
      align: "center",
      className: "font-custom text-center",
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <PermissionGuard permission={PERMISSIONS.ROLES_VIEW}>
            <Tooltip title="مشاهده">
              <Button
                type="link"
                className="text-blue-500"
                onClick={() =>
                  navigate("/dashboard/roles/view", { state: { role: row } })
                }
                icon={<FaEye />}
              />
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard permission={PERMISSIONS.ROLES_EDIT}>
            <Tooltip title="ویرایش">
              <Button
                type="link"
                className="text-green-500"
                onClick={() =>
                  navigate("/dashboard/roles/form", { state: { role: row } })
                }
                icon={<FaEdit color="#52c41a" />}
              />
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard permission={PERMISSIONS.ROLES_DELETE}>
            <Tooltip title="حذف">
              <Button
                type="link"
                className="text-red-500"
                onClick={() => handleDelete(row.id, row.name)}
                icon={<FaTrash color="#ff4d4f" />}
              />
            </Tooltip>
          </PermissionGuard>
        </div>
      ),
    },
    {
      title: "نام نقش",
      dataIndex: "name",
      key: "name",
      width: 200,
      align: "right",
      className: "font-custom text-right",
    },
    {
      title: "توضیحات",
      dataIndex: "description",
      key: "description",
      width: 300,
      align: "right",
      dir: "rtl",
      className: "font-custom text-right",
      render: (text) => <div dir="rtl">{text}</div>,
    },
    {
      title: "تعداد دسترسی‌ها",
      dataIndex: "permissionsCount",
      key: "permissionsCount",
      width: 150,
      align: "center",
      className: "font-custom text-center",
      render: (count) => count || 0,
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      align: "center",
      className: "font-custom text-center",
      render: (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("fa-IR");
      },
    },
  ];

  return (
    <div className="font-custom rtl text-right p-5">
      <h1 className="text-center text-xl mt-5 mb-5">مدیریت نقش‌ها</h1>

      <PermissionGuard permission={PERMISSIONS.ROLES_CREATE}>
        <button
          className="bg-green-700 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
          onClick={() => navigate("/dashboard/roles/form")}
        >
          + نقش جدید
        </button>
      </PermissionGuard>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={roles}
          loading={loading}
          rowKey={(record) => record.id}
          scroll={{ x: 1000 }}
          bordered
        />
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        rtl
        theme="dark"
        transition={Bounce}
      />
    </div>
  );
};

export default RolesGrid;
